import * as React from 'react';
import { cva } from 'class-variance-authority';
import { Slot } from 'radix-ui';
import type { VariantProps } from 'class-variance-authority';

import { cn } from '~/lib/utils';

const buttonVariants = cva(
  "inline-flex shrink-0 cursor-pointer items-center justify-center gap-2 rounded-md text-sm font-medium whitespace-nowrap transition-all outline-none focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 disabled:pointer-events-none disabled:opacity-50 aria-invalid:border-destructive aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
  {
    variants: {
      variant: {
        default:
          'bg-primary text-primary-foreground shadow-xs hover:bg-primary/90',
        destructive:
          'bg-destructive text-destructive-foreground shadow-xs hover:bg-destructive/90 focus-visible:ring-destructive/20 dark:bg-destructive/60 dark:focus-visible:ring-destructive/40',
        outline:
          'border border-border bg-background text-foreground shadow-xs hover:bg-accent hover:text-accent-foreground',
        secondary:
          'bg-secondary text-secondary-foreground shadow-xs hover:bg-secondary/80',
        ghost:
          'hover:bg-accent hover:text-accent-foreground dark:hover:bg-accent/50',
        muted:
          'text-muted-foreground hover:bg-accent hover:text-foreground dark:hover:bg-accent/50',
        link: 'text-primary underline-offset-4 hover:underline',
      },
      tone: {
        success: '',
        warning: '',
        info: '',
        destructive: '',
      },
      size: {
        default: 'h-9 px-4 py-2 has-[>svg]:px-3',
        xs: "h-6 gap-1 rounded-md px-2 text-xs has-[>svg]:px-1.5 [&_svg:not([class*='size-'])]:size-3",
        sm: 'h-8 gap-1.5 rounded-md px-3 has-[>svg]:px-2.5',
        lg: 'h-10 rounded-md px-6 has-[>svg]:px-4',
        icon: 'size-9',
        'icon-xs': "size-6 rounded-md [&_svg:not([class*='size-'])]:size-3",
        'icon-sm': 'size-8',
        'icon-lg': 'size-10',
      },
      /** After `size`, so a pill wins over the radius a size carries. */
      shape: {
        default: '',
        pill: 'rounded-full',
      },
    },
    compoundVariants: [
      {
        variant: ['ghost', 'muted'],
        tone: 'success',
        class: 'text-success hover:bg-success-subtle hover:text-success',
      },
      {
        variant: ['ghost', 'muted'],
        tone: 'warning',
        class: 'text-warning hover:bg-warning-subtle hover:text-warning',
      },
      {
        variant: ['ghost', 'muted'],
        tone: 'info',
        class: 'text-info hover:bg-info-subtle hover:text-info',
      },
      {
        variant: ['ghost', 'muted'],
        tone: 'destructive',
        class:
          'text-destructive hover:bg-destructive-subtle hover:text-destructive',
      },
      {
        variant: 'outline',
        tone: 'success',
        class:
          'border-success-subtle-border text-success hover:bg-success-subtle hover:text-success',
      },
      {
        variant: 'outline',
        tone: 'warning',
        class:
          'border-warning-subtle-border text-warning hover:bg-warning-subtle hover:text-warning',
      },
      {
        variant: 'outline',
        tone: 'info',
        class:
          'border-info-subtle-border text-info hover:bg-info-subtle hover:text-info',
      },
      {
        variant: 'outline',
        tone: 'destructive',
        class:
          'border-destructive-subtle-border text-destructive hover:bg-destructive-subtle hover:text-destructive',
      },
    ],
    defaultVariants: {
      variant: 'default',
      shape: 'default',
      size: 'default',
    },
  },
);

function Button({
  className,
  variant = 'default',
  tone,
  shape = 'default',
  size = 'default',
  asChild = false,
  ...props
}: React.ComponentProps<'button'> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean;
  }) {
  const Comp = asChild ? Slot.Root : 'button';

  return (
    <Comp
      data-slot="button"
      data-variant={variant}
      data-tone={tone}
      data-size={size}
      className={cn(buttonVariants({ variant, tone, shape, size, className }))}
      {...props}
    />
  );
}

export { Button, buttonVariants };
